import type { PropertyDetailListing } from '@/src/components/features/property/types';
import { sanitizeLegalDisclaimer } from '@/src/components/features/property/LegalInfoModule/legalInfoModule.utils';

type SourceInfoRow = {
  labelKey: 'sourceContactLabel' | 'sourceLicenseLabel' | 'sourceCompanyLabel' | 'sourcePhoneLabel';
  value: string;
};

export type SourceInfoDisplay = {
  brokerRows: Array<SourceInfoRow>;
  hasBrokerInfo: boolean;
  hasMlsInfo: boolean;
  mlsLogo: string;
  mlsName: string;
  disclaimerParagraphs: Array<string>;
};

const cleanString = (value: unknown): string => (typeof value === 'string' ? value.trim() : '');

const firstCleanString = (...values: Array<unknown>): string => values.map(cleanString).find(Boolean) ?? '';

const getBrokerRows = (listing: PropertyDetailListing | null | undefined): Array<SourceInfoRow> => {
  const broker = listing?.broker;
  const shouldShowBroker =
    broker?.isBrokerExclusiveListing === true ||
    broker?.isBrokerListing === true ||
    listing?.details?.isBrokerListing === true;

  if (!shouldShowBroker) return [];

  const rows: Array<SourceInfoRow> = [
    { labelKey: 'sourceContactLabel', value: cleanString(broker?.contactName) },
    {
      labelKey: 'sourceLicenseLabel',
      value: cleanString(broker?.agentLicenseType),
    },
    { labelKey: 'sourceCompanyLabel', value: cleanString(broker?.companyName) },
    {
      labelKey: 'sourcePhoneLabel',
      value: cleanString(broker?.companyPhoneNumber),
    },
  ];

  return rows.filter((row) => row.value);
};

export const getSourceInfoDisplay = (listing: PropertyDetailListing | null | undefined): SourceInfoDisplay => {
  const details = listing?.details;
  const listedBy = listing?.listedBy;
  const brokerRows = getBrokerRows(listing);
  const mlsName = firstCleanString(details?.mlsName);
  const mlsLogo = firstCleanString(details?.mlsLogo, listedBy?.mlsLogo);
  const disclaimerParagraphs = sanitizeLegalDisclaimer(firstCleanString(details?.disclaimer, listedBy?.disclaimer));
  const hasMlsInfo = details?.isMls === true && Boolean(mlsName || mlsLogo || disclaimerParagraphs.length > 0);

  return {
    brokerRows,
    hasBrokerInfo: brokerRows.length > 0,
    hasMlsInfo,
    mlsLogo,
    mlsName,
    disclaimerParagraphs,
  };
};

export const hasSourceInfoData = (listing: PropertyDetailListing | null | undefined): boolean => {
  const display = getSourceInfoDisplay(listing);
  return display.hasBrokerInfo || display.hasMlsInfo;
};
